import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import deepEqual from "fast-deep-equal";

import {
  fetchTableFieldsAtomFamily_SUSPENDS,
  fetchInterfaceProjectAtomFamily_SUSPENDS,
  fetchChatbotAtomFamily_SUSPENDS,
  fetchAgentAtomFamily_SUSPENDS,
} from "../apps";
import { AgentFetchResult, AssetType } from "../types";

/**
 * Suspended variant that computes “locked” state directly from
 * the asset-access fetch atoms.  The atom **suspends** while any
 * underlying asset call is pending.
 * Locked mode means you have no access to the asset.
 *
 * A node is locked when *any* of the following is true:
 * • Table   → fetch returns `null`
 * • Interface → fetch returns `null`
 * • Chatbot → fetch returns `null`
 * • Agent   → fetch returns `{ type: "forbidden" }`
 *
 * Other agent results (`null`, `not_found`, `success`) do NOT lock.
 */
export const isNodeLockedAtomFamily_SUSPENDS = atomFamily(
  (params: {
    assetId?: string | undefined | null;
    interfaceProjectId?: string;
    assetType: AssetType;
  }) => {
    const baseAtom = atom(async (get) => {
      const { assetId, interfaceProjectId, assetType } = params;

      const tableId = assetType === "table" ? assetId : undefined;
      const interfaceId =
        assetType === "interface" ? interfaceProjectId : undefined;
      const chatbotId = assetType === "chatbot" ? assetId : undefined;
      const agentId = assetType === "agent" ? assetId : undefined;

      /* ---------------- asset look-ups (each may suspend) ---------------- */
      const tableFields = tableId
        ? await get(fetchTableFieldsAtomFamily_SUSPENDS(tableId))
        : undefined;

      const interfaceProject = interfaceId
        ? await get(fetchInterfaceProjectAtomFamily_SUSPENDS(interfaceId))
        : undefined;

      const chatbot = chatbotId
        ? await get(fetchChatbotAtomFamily_SUSPENDS(chatbotId))
        : undefined;

      const agentResult = agentId
        ? await get(fetchAgentAtomFamily_SUSPENDS(agentId))
        : undefined;

      /* ---------------- locked evaluation ---------------- */
      const noTableAccess = tableId ? tableFields === null : false;
      const noInterfaceAccess = interfaceId ? interfaceProject === null : false;
      const noChatbotAccess = chatbotId ? chatbot === null : false;

      const noAgentAccess =
        (agentResult as AgentFetchResult | null | undefined)?.type ===
        "forbidden";

      return (
        noTableAccess || noInterfaceAccess || noChatbotAccess || noAgentAccess
      );
    });

    baseAtom.debugLabel = `isNodeLockedAtomFamily_SUSPENDS(${JSON.stringify(
      params,
    )})`;
    return baseAtom;
  },
  deepEqual,
);
